"use client";

import useSWR from "swr";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { FolderPlus } from "lucide-react";
import { apiClient } from "@/lib/api-client";

interface CollectionSummary {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  itemCount: number;
  updatedAt: Date | string;
}

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

type FilterId = string | "__all" | "__new" | "__recent" | "__uncollected";

interface CollectionListProps {
  activeFilter: FilterId;
  onFilterChange: (filter: FilterId) => void;
  onNewCollection: () => void;
}

export function CollectionList({
  activeFilter,
  onFilterChange,
  onNewCollection,
}: CollectionListProps) {
  const { data } = useSWR<CollectionSummary[]>("/api/collections", fetcher);

  const collections = data ?? [];

  const chipClass = (id: FilterId) =>
    `cursor-pointer transition-colors ${
      activeFilter === id
        ? "bg-primary text-primary-foreground hover:bg-primary/90"
        : ""
    }`;

  return (
    <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 scrollbar-thin">
      <Badge
        variant={activeFilter === "__all" ? "default" : "secondary"}
        className={chipClass("__all")}
        onClick={() => onFilterChange("__all")}
      >
        All
      </Badge>
      <Badge
        variant={activeFilter === "__new" ? "default" : "outline"}
        className={chipClass("__new")}
        onClick={() => onFilterChange("__new")}
      >
        New
      </Badge>
      <Badge
        variant={activeFilter === "__recent" ? "default" : "outline"}
        className={chipClass("__recent")}
        onClick={() => onFilterChange("__recent")}
      >
        Recently Updated
      </Badge>
      <Badge
        variant={activeFilter === "__uncollected" ? "default" : "outline"}
        className={chipClass("__uncollected")}
        onClick={() => onFilterChange("__uncollected")}
      >
        Uncollected
      </Badge>

      {collections.length > 0 && (
        <div className="w-px h-4 bg-border shrink-0" />
      )}

      {collections.map((col) => (
        <Badge
          key={col.id}
          variant={activeFilter === col.id ? "default" : "secondary"}
          className={`${chipClass(col.id)} shrink-0`}
          onClick={() => onFilterChange(col.id)}
        >
          {col.icon && <span className="mr-1">{col.icon}</span>}
          {col.name}
          <span className="ml-1 opacity-60">{col.itemCount}</span>
        </Badge>
      ))}

      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs shrink-0 text-muted-foreground"
        onClick={onNewCollection}
      >
        <FolderPlus className="h-3 w-3 mr-1" />
        Collection
      </Button>
    </div>
  );
}

export type { FilterId };
